import type { SFC } from 'react';
import { ListGroupItem } from 'react-bootstrap';
import type { ConnectDragSource} from 'react-dnd';
import { DragSource } from 'react-dnd';

import { Action } from '../../../../typings/permissions';
import { userHasPermissionForPolicyType } from '../../../helpers/permissions';
import type { LicensingRule, Policy } from '../../../types';
import Button from '../../generic/Button';
import Icon, { icons } from '../../generic/Icon';
import Toolip from '../../generic/Tooltip';

type RuleItemProps = {
  policy: Policy;
  rule: Partial<LicensingRule>;
  onEdit?: () => void;
  onRemove?: () => void;
  onLicenseClick: (licenseId: string | null, ruleId: number | null) => void;
};

type DragSourceProps = {
  connectDragSource: ConnectDragSource;
  isDragging: boolean;
};

//  Returns the text for the tooltip based on the specified depth condition.
function getDepthTooltip(depthCondition?: 'DIRECT' | 'DEEP'): string {
  if (depthCondition === 'DIRECT') {
    return 'found in your code';
  }
  if (depthCondition === 'DEEP') {
    return 'found in dependency';
  }
  return '';
}

function getNameConditionTooltip(nameCondition?: string): string {
  return nameCondition
    ? `name contains ${nameCondition}`
    : '';
}

function getLinkingTooltip(linkingCondition?: 'STATIC' | 'DYNAMIC'): string {
  if (linkingCondition === 'STATIC') {
    return 'statically linked';
  }
  if (linkingCondition === 'DYNAMIC') {
    return 'dynamically linked';
  }
  return '';
}

//  Build the condition tooltip.
function getConditionalTooltip(
  depthCondition?: 'DIRECT' | 'DEEP',
  nameCondition?: string,
  linkingCondition?: 'STATIC' | 'DYNAMIC',
): string {
  return [
    getDepthTooltip(depthCondition),
    getNameConditionTooltip(nameCondition),
    getLinkingTooltip(linkingCondition),
  ]
    .filter(value => !!value)
    .join(' & ');
}

const InnerRuleItem: SFC<RuleItemProps & DragSourceProps> = props => {
  if (props.isDragging) {
    return <span />;
  }
  const { depthCondition, nameCondition, linkingCondition } = props.rule;

  const hasCondition = !!(depthCondition || nameCondition || linkingCondition);

  const conditionalTooltip = hasCondition
    ? `If ${getConditionalTooltip(depthCondition, nameCondition, linkingCondition)}.`
    : '';

  const content = (
    <span>
      <h4>
        {props.rule.licenseId}
        {hasCondition && (
          <Toolip
            tooltip={conditionalTooltip}
            showTooltip={hasCondition}
          >
            <span className="policy-rule-condition margin-left-xs">
              <Icon icon={icons.questionCircle} />
            </span>
          </Toolip>
        )}
      </h4>
      <p style={{textOverflow: 'ellipsis', width: '100%'}}>
        {props.rule.notes}
      </p>
    </span>
  );

  const licenseId = props.rule.licenseId;

  return props.connectDragSource(
    <div>
      <ListGroupItem className="draggable clearfix policy-rule-item">
        {content}
        <div className="policy-rule-item-controls" style={{ position: 'absolute', right: '4px', top: '8px' }}>
          <Button
            size="micro"
            type="secondary"
            icon={icons.eye}
            onClick={() => props.onLicenseClick(licenseId ?? null, props.rule.ruleId ?? null)}
          />
          {userHasPermissionForPolicyType(Action.Edit, props.policy.type) && props.onEdit && (
            <>
              {' '}
              <Button
                size="micro"
                type="secondary"
                icon={icons.pencil}
                onClick={props.onEdit}
              />
              {' '}
              <Button
                size="micro"
                type="secondary"
                icon={icons.trash}
                onClick={() => props.onRemove?.()}
              />
            </>
          )}
        </div>
      </ListGroupItem>
    </div>,
  );
};

export type RuleItemDragProps = {
  policy: Policy;
  rule: LicensingRule;
};

export const RuleItem = DragSource<RuleItemProps>(
  'rule-item',
  {
    beginDrag(props: RuleItemProps): RuleItemDragProps {
      return { policy: props.policy, rule: props.rule };
    },
    canDrag(props) {
      return userHasPermissionForPolicyType(Action.Edit, props.policy.type);
    },
  },
  (connect, monitor) => ({
    connectDragSource: connect.dragSource(),
    isDragging: monitor.isDragging(),
    didDrop: monitor.didDrop(),
  }),
)(InnerRuleItem);
